/*
 * File: LoginScreen.tsx
 * Project: a-a
 * File Created: Monday, 7th October 2024 7:52:01 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Monday, 2nd February 2026 10:59:53 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React, { useEffect, useState } from 'react';
import styles from './LoginScreen.module.css';
import CustomButton from '../../components/shared/CustomButton/CustomButton';
import zeplinColor from '../../theme/Color';
import CustomTextField from '../../components/shared/CustomTextField/CustomTextField';

import EntryLayoutScreen from '../../components/shared/EntryLayout/EntryLayout';

import CustomCheckbox from '../../components/shared/CustomCheckbox/CustomCheckbox';
import RoutePaths from '../../routes/RoutePaths';
import { Link, useNavigate } from 'react-router-dom';
import { validatePassword } from '../../helper/validate';
import { AuthService } from '../../service/auth.service';
import { Constants } from '../../utilities/constants';
import { useAppDispatch } from '../../store/hooks';
import { setRole } from '../../store/slices/authSlice';
import { useInput } from '../../customHook/useInput';
import validator from 'validator';

import CustomPassword from '../../components/shared/CustomPassword/CustomPassword';

// type err={
//     "UserNotFoundException":string,
//     "NotAuthorizedException":string
// }

const LoginScreen = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const expiryTime = sessionStorage.getItem('loginExpiry') || '';
  // const isMSLogin = sessionStorage.getItem('ms_login') || '';
  // const logOutUrl = sessionStorage.getItem('logOutUrl') || '';

  // console.log(new Date() < new Date(expiryTime), 'expiry');
  sessionStorage.removeItem('reset-user');

  const [showPassword, setShowPassword] = useState(false);

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const {
    handleChange: handleEmailChange,
    handleBlur: handleEmailBlur,
    value: userName,
    invalid: invalidEmail,
    didChange: didEmailChange,
  } = useInput('', validator.isEmail);
  const {
    handleChange: handlePasswordChange,
    handleBlur: handlePasswordBlur,
    value: password,
    invalid: invalidPassword,
    didChange: didPasswordChange,
  } = useInput('', (text) => validatePassword(text).valid);

  const valid = !invalidEmail && password.length;
  // const invalidEmail=didChange && validateEmail(value.userName) ? 'not valid email':''
  const [error, setError] = useState('');
  const [errorPassword, setErrorPassword] = useState('');
  const [open, setOpen] = React.useState(false);
  const [alert, setAlert] = useState('');
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const login = async () => {
    try {
      handleOpen();
      const res = await AuthService.login({ userName, password });
      // console.log(res?.httpStatusCode == 200, res);
      if (res) {
        dispatch(setRole(res.role));
        const huddleInfo = await AuthService.huddle_info(res.AuthenticationResult?.IdToken);

        sessionStorage.setItem('current-user', userName);
        sessionStorage.setItem('user-token', JSON.stringify(res.AuthenticationResult));
        sessionStorage.setItem('SFToken', JSON.stringify(res.isSFLinked || huddleInfo?.isSFLinked));
        sessionStorage.setItem('MSToken', JSON.stringify(res.isMSLogin));
        sessionStorage.setItem('isSlackConnected', huddleInfo?.isSlackLinked);
        sessionStorage.setItem('isGoogleConnected', huddleInfo?.isGoogleLinked);
        sessionStorage.setItem('isZoomConnected', huddleInfo?.isZoomLinked);
        sessionStorage.setItem('user-name', `${res.firstName}`);
        const now = new Date();
        const fiveHoursLater = new Date(now.getTime() + 60 * 60 * 1000 * 5);
        const oneHourLater = new Date(now.getTime() + 60 * 60 * 1000);
        // Save in sessionStorage as a string
        sessionStorage.setItem('loginExpiry', fiveHoursLater.toString());
        sessionStorage.setItem('refreshToken', oneHourLater.toString());
        navigate(RoutePaths.Home);
      }
    } catch (err: any) {
      // console.log(err === 'Network Error');
      const error = err?.response?.data?.name ? err?.response?.data?.name : err?.response?.data; // without splice. original data
      // console.log(error === Constants.ErrorType.NotAuthourized, error);
      if (err === 'Network Error') {
        setAlert('Network Error');
        setError('');
        setErrorPassword('');
      } else if (err?.response?.data) {
        setError(String(error));
        setErrorPassword('');
      } else if (error === Constants.ErrorType.NotAuthourized) {
        setErrorPassword(Constants.ErrorText.NotAuthorizedException);
        setError('');
      } else {
        setErrorPassword('');
        setError(Constants.ErrorText.UserNotFoundException);
      }
    } finally {
      handleClose();
    }
  };

  const children = (
    <div className={styles['login-input']}>
      <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 24 }}>
        <CustomTextField
          style={{ width: '50%', margin: 'auto' }}
          onBlur={handleEmailBlur}
          onChange={handleEmailChange}
          name='userName'
          text={userName}
          error={error ? error : invalidEmail && didEmailChange ? 'Please enter valid email' : ''}
          label={'Email address'}
          type='email'
          tabindex={1}
        />
        <CustomPassword
          style={{ width: '50%', margin: 'auto' }}
          showPassword={showPassword}
          handleClickShowPassword={handleClickShowPassword}
          handleMouseDownPassword={handleMouseDownPassword}
          handleMouseUpPassword={handleMouseUpPassword}
          onBlur={handlePasswordBlur}
          onChange={handlePasswordChange}
          name='password'
          text={password}
          label='Password'
          error={errorPassword}
          tabindex={2}
        />
      </div>
      {/* </div> */}
      <div className={styles.helperlogin}>
        {' '}
        {/* <CustomCheckbox label='Remember me' /> */}
        {/* <Typography variant='h1' color='primary'>Forgot Password?</Typography> */}
        <Link to={RoutePaths.ForgotPassword}>
          <CustomButton
            disabled={false}
            variant='text'
            text={'Forgot Password?'}
            textStyle={{ color: zeplinColor.Brand1 }}
            onClick={() => {}}
            tabindex={3}
          />
        </Link>
      </div>
      <CustomButton
        disabled={valid ? false : true}
        onClick={login}
        variant='contained'
        color={valid ? 'primary' : 'customDisable'}
        style={{
          width: '50%',
          borderRadius: '14px',
          height: 56,
          '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
        }}
        textStyle={{ color: valid ? zeplinColor.Neutral1 : zeplinColor.Neutral8 }}
        text={'Log In'}
        // tabindex={4}
      />
    </div>
  );

  const alternative = (
    <div
      style={{ margin: '0px auto 0px', width: '50%', display: 'flex', justifyContent: 'center' }}
    >
      <Link
        to={`https://${process.env.REACT_APP_M365}`}
        style={{ textDecoration: 'none', width: '100%' }}
      >
        <CustomButton
          disabled={false}
          variant='outlined'
          color='customSecondary'
          style={{
            color: zeplinColor.BasicBlack,
            width: '100%',
            margin: 'auto',
            borderRadius: '14px',
            height: 56,
            borderWidth: 1,
            borderColor: zeplinColor.Neutral9,
            display: 'flex',
            gap: '16px',
            '&:hover': {
              backgroundColor: zeplinColor.Neutral5,
            },
          }}
          text={'Continue with Microsoft'}
          icon={<img src={'/assets/microsoft-logo.svg'} />}
          onClick={() => {}}
          // tabindex={5}
        />
      </Link>
    </div>
  );

  useEffect(() => {
    if (expiryTime) {
      if (new Date() < new Date(expiryTime)) {
        // console.log('Navigating to Home');
        navigate(RoutePaths.Home);
      }
    }
  }, []);
  return (
    <EntryLayoutScreen
      alert={alert}
      header={'Log in'}
      subheader='Enter your email and password to continue.'
      input={children}
      alternative={alternative}
      footerText='Don’t have an account?  '
      footerButtonText='Create Account'
      path={RoutePaths.Register}
      clickAction={() => {}}
      handleCloseModal={handleClose}
      open={open}
    />
  );
};

export default LoginScreen;
